// src/components/TopBarAdmin/TopBarAdmin.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  FaCalendarCheck,
  FaEnvelope,
  FaGlobeEurope,
  FaMoon,
  FaSignOutAlt,
  FaSun,
  FaVoteYea,
} from "react-icons/fa";

import { MdOutlineMessage } from "react-icons/md";

import { useUser } from "../../pages/Home/UserContext";

import { useMessage } from "../../hooks/MessageContext";
import { useContactForm } from "../../hooks/ContactFormContext";
import { useVote } from "../../hooks/VoteContext";
import { useConvocation } from "../../hooks/ConvocationContext";

import { toAbsoluteUserPhotoUrl } from "../../services/userPhotoServices";

const PAGE_TITLES = [
  {
    match: "/appli/dashboard",
    label: "Tableau de bord",
  },
  {
    match: "/appli/utilisateurs",
    label: "Utilisateurs",
  },
  {
    match: "/appli/badge-users",
    label: "Badges numériques",
  },
  {
    match: "/appli/ligue-commissions",
    label: "Ligue & Commissions",
  },
  {
    match: "/appli/formation-dirigeants",
    label: "Formation des dirigeants",
  },
  {
    match: "/appli/clubs-admin",
    label: "Clubs Nouvelle-Aquitaine",
  },
  {
    match: "/appli/groupes",
    label: "Groupes",
  },
  {
    match: "/appli/acteurs",
    label: "Acteurs",
  },
  {
    match: "/appli/calendrier",
    label: "Calendrier",
  },
  {
    match: "/appli/convocations-admin",
    label: "Détail des convocations",
  },
  {
    match: "/appli/convocations",
    label: "Convocations",
  },
  {
    match: "/appli/inscriptions-cn",
    label: "Inscriptions Centre National",
  },
  {
    match: "/appli/licencies-inscrits",
    label: "Licenciés inscrits",
  },
  {
    match: "/appli/expense-claims",
    label: "Fiches de frais",
  },
  {
    match: "/appli/votes-admin",
    label: "Votes & Sondages",
  },
  {
    match: "/appli/notifications",
    label: "Notifications",
  },
];

const cx = (...classes) =>
  classes.filter(Boolean).join(" ");

function getPageTitle(pathname) {
  const found = PAGE_TITLES.find(
    (item) =>
      pathname.startsWith(item.match),
  );

  return found?.label || "Administration";
}

function formatCount(count) {
  const numericCount =
    Number(count) || 0;

  return numericCount > 99
    ? "99+"
    : numericCount;
}

function NotificationBadge({
  count,
  className = "",
}) {
  const numericCount =
    Number(count) || 0;

  if (numericCount <= 0) {
    return null;
  }

  return (
    <span
      className={cx(
        [
          "absolute",
          "-right-1.5 -top-1.5",
          "inline-flex",
          "min-h-[18px]",
          "min-w-[18px]",
          "items-center",
          "justify-center",
          "rounded-full",
          "bg-red-600",
          "px-1",
          "text-[9px]",
          "font-semibold",
          "leading-none",
          "text-white",
          "shadow-sm",
          "ring-2",
          "ring-white",
          "dark:ring-slate-950",
        ].join(" "),
        className,
      )}
    >
      {formatCount(numericCount)}
    </span>
  );
}

function IconActionButton({
  label,
  count = 0,
  active = false,
  loading = false,
  onClick,
  children,
  className = "",
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cx(
        [
          "relative",
          "flex h-10 w-10",
          "shrink-0",
          "items-center",
          "justify-center",
          "rounded-xl",
          "border",
          "text-sm",
          "shadow-sm",
          "transition",
          "duration-200",
          "active:scale-95",
          "focus-visible:outline-none",
          "focus-visible:ring-4",
          "focus-visible:ring-blue-500/20",
        ].join(" "),
        active
          ? [
              "border-blue-200",
              "bg-blue-50",
              "text-blue-700",
              "dark:border-blue-800",
              "dark:bg-blue-950/50",
              "dark:text-blue-300",
            ].join(" ")
          : [
              "border-slate-200",
              "bg-white",
              "text-slate-700",
              "hover:border-slate-300",
              "hover:bg-slate-50",
              "dark:border-slate-700",
              "dark:bg-slate-900",
              "dark:text-slate-200",
              "dark:hover:border-slate-600",
              "dark:hover:bg-slate-800",
            ].join(" "),
        loading
          ? "animate-pulse"
          : "",
        className,
      )}
    >
      {children}

      <NotificationBadge
        count={count}
      />
    </button>
  );
}

function MobileActionButton({
  label,
  count = 0,
  active = false,
  loading = false,
  onClick,
  children,
  className = "",
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cx(
        [
          "relative",
          "flex h-full",
          "min-w-[60px]",
          "shrink-0",
          "flex-col",
          "items-center",
          "justify-center",
          "gap-0.5",
          "px-1.5",
          "text-[9px]",
          "font-medium",
          "transition",
          "duration-200",
          "active:bg-slate-100",
          "dark:active:bg-slate-800",
        ].join(" "),
        active
          ? [
              "text-blue-700",
              "dark:text-blue-300",
            ].join(" ")
          : [
              "text-slate-600",
              "dark:text-slate-300",
            ].join(" "),
        loading
          ? "animate-pulse"
          : "",
        className,
      )}
    >
      <span
        className="
          relative
          flex h-5 w-5
          items-center
          justify-center
          text-sm
        "
      >
        {children}

        <NotificationBadge
          count={count}
          className="
            -right-2.5
            -top-2
            ring-slate-50
            dark:ring-slate-900
          "
        />
      </span>

      <span className="whitespace-nowrap">
        {label}
      </span>
    </button>
  );
}

export default function TopBarAdmin() {
  const {
    user,
    updateUser,
  } = useUser();

  const {
    unreadMessageCount,
  } = useMessage();

  const {
    unreadCountContactForm,
  } = useContactForm();

  const {
    pendingVoteCount,
    pendingVotesLoading,
    refreshPendingVotes,
  } = useVote();

  const {
    pendingFutureConvocationCount,
    loadingConvocations,
    refreshPendingConvocations,
  } = useConvocation();

  const navigate = useNavigate();
  const location = useLocation();

  const [
    mounted,
    setMounted,
  ] = useState(false);

  const [
    theme,
    setTheme,
  ] = useState(() =>
    document.documentElement.classList.contains(
      "dark",
    )
      ? "dark"
      : "light",
  );

  const userId =
    user?.userId ||
    user?.id ||
    null;

  const userRoleId = Number(
    user?.role_id,
  );

  const isAdmin =
    userRoleId === 1;

  const pageTitle = useMemo(
    () =>
      getPageTitle(
        location.pathname,
      ),
    [location.pathname],
  );

  const avatarSrc = useMemo(() => {
    const photoPath =
      user?.photoURL ||
      user?.photo_url ||
      user?.photoUrl;

    return (
      toAbsoluteUserPhotoUrl(
        photoPath,
      ) ||
      "/img/user_avatar.jpg"
    );
  }, [
    user?.photoURL,
    user?.photo_url,
    user?.photoUrl,
  ]);

  useEffect(() => {
    setMounted(true);

    if (user) {
      return;
    }

    const storedUserInfo =
      localStorage.getItem(
        "userInfo",
      );

    if (!storedUserInfo) {
      return;
    }

    try {
      const parsedUser =
        JSON.parse(
          storedUserInfo,
        );

      if (parsedUser) {
        updateUser(parsedUser);
      }
    } catch (error) {
      console.error(
        "Erreur lors du parsing des informations utilisateur :",
        error,
      );
    }
  }, [
    user,
    updateUser,
  ]);

  /*
   * Actualise les compteurs au chargement
   * et lors du changement de page admin.
   */
  useEffect(() => {
    if (!userId) {
      return;
    }

    refreshPendingVotes?.();

    refreshPendingConvocations?.({
      silent: true,
    });
  }, [
    location.pathname,
    refreshPendingConvocations,
    refreshPendingVotes,
    userId,
  ]);

  /*
   * Actualise les alertes lorsque l'utilisateur
   * revient sur l'onglet.
   */
  useEffect(() => {
    if (!userId) {
      return undefined;
    }

    const handleWindowFocus = () => {
      refreshPendingVotes?.();

      refreshPendingConvocations?.({
        silent: true,
      });
    };

    window.addEventListener(
      "focus",
      handleWindowFocus,
    );

    return () => {
      window.removeEventListener(
        "focus",
        handleWindowFocus,
      );
    };
  }, [
    refreshPendingConvocations,
    refreshPendingVotes,
    userId,
  ]);

  const handleLogout = () => {
    localStorage.removeItem(
      "userInfo",
    );

    localStorage.removeItem(
      "token",
    );

    sessionStorage.removeItem(
      "token",
    );

    updateUser(null);

    navigate("/login");
  };

  const toggleTheme = () => {
    const nextTheme =
      document.documentElement.classList.toggle(
        "dark",
      )
        ? "dark"
        : "light";

    localStorage.setItem(
      "theme",
      nextTheme,
    );

    setTheme(nextTheme);
  };

  const goToPublicSite = () =>
    navigate("/");

  const goToProfile = () =>
    navigate(
      "/profile-utilisateur",
    );

  const goToMessages = () =>
    navigate("/messagerie");

  const goToContactMessages = () =>
    navigate(
      "/appli/notifications",
    );

  const goToConvocations = () =>
    navigate("/mesconvocations");

  const goToVotes = () =>
    navigate(
      "/votes-utilisateur",
    );

  return (
    <header
      className="
        fixed inset-x-0 top-0
        z-[60]
        h-[116px]
        border-b
        border-slate-200/80
        bg-white/95
        shadow-sm
        backdrop-blur-xl
        dark:border-slate-800/80
        dark:bg-slate-950/95
        md:h-[88px]
      "
    >
      {/* =================================================== */}
      {/* LIGNE PRINCIPALE                                    */}
      {/* =================================================== */}

      <div
        className="
          mx-auto
          flex h-[68px]
          max-w-[1800px]
          items-center
          justify-between
          gap-2
          px-3
          sm:px-4
          md:h-full
          md:gap-4
          md:px-5
          lg:px-6
        "
      >
        {/* Partie gauche */}
        <div
          className="
            flex min-w-0
            flex-1 items-center
            gap-2.5
            sm:gap-4
          "
        >
          <button
            type="button"
            onClick={goToPublicSite}
            className="
              flex h-10 w-10
              shrink-0 items-center
              justify-center
              overflow-hidden
              rounded-xl
              border border-slate-200
              bg-white
              shadow-sm
              transition
              hover:border-blue-300
              dark:border-slate-700
              dark:bg-slate-900
              md:h-12
              md:w-12
              md:rounded-2xl
            "
            title="Accéder au site public"
            aria-label="Accéder au site public"
          >
            <img
              src="/logo_NA_fondblanc.jpg"
              alt="Ligue Judo Nouvelle-Aquitaine"
              className="
                h-full w-full
                object-contain
              "
            />
          </button>

          <div className="min-w-0">
            <p
              className="
                text-[8px]
                font-medium
                uppercase
                tracking-[0.18em]
                text-slate-400
                dark:text-slate-500
                sm:text-[10px]
                sm:tracking-[0.24em]
              "
            >
              Espace administrateur
            </p>

            <h1
              className="
                truncate
                text-sm
                font-semibold
                text-slate-900
                dark:text-white
                sm:text-base
                md:text-lg
              "
            >
              {pageTitle}
            </h1>

            <p
              className="
                hidden text-xs
                text-slate-500
                dark:text-slate-400
                lg:block
              "
            >
              Ligue Judo
              Nouvelle-Aquitaine
            </p>
          </div>
        </div>

        {/* Actions desktop */}
        <div
          className="
            hidden shrink-0
            items-center gap-2
            md:flex
          "
        >
          {userId ? (
            <IconActionButton
              label="Messagerie privée"
              count={
                unreadMessageCount
              }
              onClick={goToMessages}
            >
              <FaEnvelope />
            </IconActionButton>
          ) : null}

          {isAdmin ? (
            <IconActionButton
              label="Messages du formulaire"
              count={
                unreadCountContactForm
              }
              onClick={
                goToContactMessages
              }
            >
              <MdOutlineMessage className="text-lg" />
            </IconActionButton>
          ) : null}

          {userId ? (
            <IconActionButton
              label="Mes convocations"
              count={
                pendingFutureConvocationCount
              }
              active={
                pendingFutureConvocationCount >
                0
              }
              loading={
                loadingConvocations
              }
              onClick={
                goToConvocations
              }
            >
              <FaCalendarCheck />
            </IconActionButton>
          ) : null}

          {userId ? (
            <IconActionButton
              label="Votes électroniques"
              count={
                pendingVoteCount
              }
              active={
                pendingVoteCount > 0
              }
              loading={
                pendingVotesLoading
              }
              onClick={goToVotes}
            >
              <FaVoteYea />
            </IconActionButton>
          ) : null}

          <button
            type="button"
            onClick={goToPublicSite}
            className="
              inline-flex h-10
              items-center gap-2
              rounded-xl
              border border-slate-200
              bg-white
              px-3
              text-sm font-medium
              text-slate-700
              shadow-sm
              transition
              hover:border-blue-300
              hover:bg-blue-50
              hover:text-blue-700
              dark:border-slate-700
              dark:bg-slate-900
              dark:text-slate-200
              dark:hover:border-blue-800
              dark:hover:bg-blue-950/40
              dark:hover:text-blue-300
            "
            title="Voir le site public"
          >
            <FaGlobeEurope />

            <span className="hidden lg:inline">
              Site public
            </span>
          </button>

          <IconActionButton
            label="Changer le thème"
            onClick={toggleTheme}
          >
            {mounted &&
            theme === "dark" ? (
              <FaSun />
            ) : (
              <FaMoon />
            )}
          </IconActionButton>

          {userId ? (
            <button
              type="button"
              onClick={goToProfile}
              className="
                flex h-11
                items-center gap-2
                rounded-xl
                border border-slate-200
                bg-white
                p-1
                pr-2.5
                text-left
                shadow-sm
                transition
                hover:border-blue-300
                hover:bg-slate-50
                dark:border-slate-700
                dark:bg-slate-900
                dark:hover:bg-slate-800
              "
              title="Profil utilisateur"
            >
              <img
                src={avatarSrc}
                alt="Avatar utilisateur"
                className="
                  h-9 w-9
                  rounded-lg
                  object-cover
                  ring-1
                  ring-slate-200
                  dark:ring-slate-700
                "
                onError={(event) => {
                  event.currentTarget.src =
                    "/img/user_avatar.jpg";
                }}
              />

              <div
                className="
                  hidden max-w-[130px]
                  min-w-0
                  xl:block
                "
              >
                <div
                  className="
                    truncate
                    text-xs font-semibold
                    text-slate-900
                    dark:text-white
                  "
                >
                  {user?.prenom || ""}{" "}
                  {user?.nom || ""}
                </div>

                <div
                  className="
                    truncate
                    text-[10px]
                    text-slate-500
                    dark:text-slate-400
                  "
                >
                  Profil & badges
                </div>
              </div>
            </button>
          ) : null}

          {userId ? (
            <IconActionButton
              label="Déconnexion"
              onClick={handleLogout}
              className="
                border-red-200
                bg-red-50
                text-red-700
                hover:border-red-300
                hover:bg-red-100
                dark:border-red-500/20
                dark:bg-red-500/10
                dark:text-red-300
                dark:hover:bg-red-500/20
              "
            >
              <FaSignOutAlt />
            </IconActionButton>
          ) : null}
        </div>

        {/* Profil et déconnexion mobile */}
        <div
          className="
            flex shrink-0
            items-center gap-1.5
            md:hidden
          "
        >
          {userId ? (
            <button
              type="button"
              onClick={goToProfile}
              className="
                flex h-9 w-9
                shrink-0
                items-center
                justify-center
                rounded-full
                ring-2
                ring-slate-200
                transition
                active:scale-95
                dark:ring-slate-700
              "
              title="Profil utilisateur"
              aria-label="Profil utilisateur"
            >
              <img
                src={avatarSrc}
                alt="Avatar utilisateur"
                className="
                  h-full w-full
                  rounded-full
                  object-cover
                "
                onError={(event) => {
                  event.currentTarget.src =
                    "/img/user_avatar.jpg";
                }}
              />
            </button>
          ) : null}

          {userId ? (
            <button
              type="button"
              onClick={handleLogout}
              className="
                flex h-9 w-9
                shrink-0
                items-center
                justify-center
                rounded-xl
                border border-red-200
                bg-red-50
                text-sm
                text-red-700
                transition
                active:scale-95
                dark:border-red-500/20
                dark:bg-red-500/10
                dark:text-red-300
              "
              title="Déconnexion"
              aria-label="Déconnexion"
            >
              <FaSignOutAlt />
            </button>
          ) : null}
        </div>
      </div>

      {/* =================================================== */}
      {/* BARRE D’ACTIONS MOBILE                              */}
      {/* =================================================== */}

      <nav
        aria-label="Raccourcis administrateur"
        className="
          flex h-[48px]
          w-full
          items-stretch
          overflow-x-auto
          border-t
          border-slate-200/80
          bg-slate-50/95
          px-1
          overscroll-x-contain
          dark:border-slate-800
          dark:bg-slate-900/95
          md:hidden
          [scrollbar-width:none]
          [&::-webkit-scrollbar]:hidden
        "
      >
        {/* Toujours visible et placé en premier */}
        <MobileActionButton
          label="Site public"
          active
          onClick={goToPublicSite}
          className="
            min-w-[72px]
            text-blue-700
            dark:text-blue-300
          "
        >
          <FaGlobeEurope />
        </MobileActionButton>

        {userId ? (
          <MobileActionButton
            label="Messages"
            count={
              unreadMessageCount
            }
            onClick={goToMessages}
          >
            <FaEnvelope />
          </MobileActionButton>
        ) : null}

        {isAdmin ? (
          <MobileActionButton
            label="Formulaire"
            count={
              unreadCountContactForm
            }
            onClick={
              goToContactMessages
            }
          >
            <MdOutlineMessage className="text-base" />
          </MobileActionButton>
        ) : null}

        {userId ? (
          <MobileActionButton
            label="Convocations"
            count={
              pendingFutureConvocationCount
            }
            active={
              pendingFutureConvocationCount >
              0
            }
            loading={
              loadingConvocations
            }
            onClick={
              goToConvocations
            }
          >
            <FaCalendarCheck />
          </MobileActionButton>
        ) : null}

        {userId ? (
          <MobileActionButton
            label="Votes"
            count={pendingVoteCount}
            active={
              pendingVoteCount > 0
            }
            loading={
              pendingVotesLoading
            }
            onClick={goToVotes}
          >
            <FaVoteYea />
          </MobileActionButton>
        ) : null}

        <MobileActionButton
          label={
            theme === "dark"
              ? "Thème clair"
              : "Thème sombre"
          }
          onClick={toggleTheme}
        >
          {mounted &&
          theme === "dark" ? (
            <FaSun />
          ) : (
            <FaMoon />
          )}
        </MobileActionButton>
      </nav>
    </header>
  );
}